import React from 'react';

import {ArrowRightFromSquare, CircleInfo, Key, Plus, Xmark} from '@gravity-ui/icons';
import {Button, Icon, IconData, Menu} from '@gravity-ui/uikit';
import _ from 'lodash';

import {OrganizationIcon} from '../OrganizationIcon/OrganizationIcon';
import {block} from '../cn';

import {UserDropdownAvatar} from './UserDropdownAvatar/UserDropdownAvatar';
import {UserDropdownUser} from './UserDropdownUser/UserDropdownUser';
import i18n from './i18n';
import {Account, LoginHistoryEntry, User} from './models';
import {PassportLoginAction, submitPassportForm} from './submitPassportForm';
import {UserDropdownEventType, UserMenuProps} from './types';
import {
    getAvatarUrl,
    getPassportHref,
    getPassportUsers,
    getUserDescription,
    getUserName,
    isUserSignIn,
} from './utils';

import './UserMenuContent.scss';

export interface UserMenuContentProps
    extends Pick<
        UserMenuProps,
        | 'user'
        | 'accounts'
        | 'loginHistory'
        | 'organizations'
        | 'currentOrgId'
        | 'actions'
        | 'internal'
        | 'cloudAuth'
        | 'oauthUrl'
        | 'baseUrl'
        | 'onSelectOrganization'
        | 'onDeleteHistoryEntry'
        | 'onOpenUserInfo'
        | 'onEvent'
        | 'showSsoLogin'
    > {
    getRetpathUrl?: (event: UserDropdownEventType) => string | undefined;
    organizationIcon?: IconData;
    organizationIconSize?: number;
}

const b = block('user-menu-content');

export class UserMenuContent extends React.Component<UserMenuContentProps> {
    render() {
        const {user, loginHistory} = this.props;

        if (!isUserSignIn(user)) {
            return null;
        }

        return (
            <div className={b()}>
                {this.renderCurrentAccount()}
                {this.isCloudAuth() && loginHistory
                    ? this.renderLoginHistory()
                    : this.renderPassportUsers()}
                {this.renderLoginButton()}
            </div>
        );
    }

    private renderCurrentAccount() {
        const currentUser = this.getCurrentUser();

        return (
            <div className={b('current-account')}>
                {this.renderUser(currentUser)}
                {this.renderActions()}
                {this.renderOrganizations()}
            </div>
        );
    }

    private renderActions() {
        const {actions} = this.props;

        if (!Array.isArray(actions) || actions.length === 0) {
            return null;
        }

        return (
            <Menu className={b('actions')}>
                {actions.map(({key, label, href, onClick}) => (
                    <Menu.Item
                        className={b('actions-item')}
                        key={key}
                        href={href}
                        onClick={onClick}
                    >
                        <span className={b('action-name')}>{label}</span>
                    </Menu.Item>
                ))}
            </Menu>
        );
    }

    private renderOrganizations() {
        const {
            organizations,
            currentOrgId,
            onSelectOrganization,
            organizationIcon,
            organizationIconSize,
        } = this.props;

        if (!Array.isArray(organizations) || organizations.length === 0) {
            return null;
        }

        return (
            <Menu className={b('organizations')}>
                {organizations.map((organization) => {
                    const {id, title, name} = organization;
                    const isCurrentOrg = id === currentOrgId;

                    return (
                        <Menu.Item
                            className={b('organizations-item')}
                            key={id}
                            icon={
                                <OrganizationIcon
                                    orgId={id}
                                    size={24}
                                    icon={organizationIcon}
                                    iconSize={organizationIconSize}
                                />
                            }
                            active={isCurrentOrg}
                            onClick={
                                !isCurrentOrg && onSelectOrganization
                                    ? (event) => {
                                          event.stopPropagation();
                                          onSelectOrganization(organization);
                                      }
                                    : undefined
                            }
                        >
                            <span className={b('organization-name')}>{title || name}</span>
                        </Menu.Item>
                    );
                })}
            </Menu>
        );
    }

    private renderPassportUsers() {
        const {accounts = []} = this.props;
        const {uid: currentUserUid, yandexuid, avatarHost} = this.getCurrentUser();

        const passportUsers = getPassportUsers(accounts, yandexuid, avatarHost);
        const otherUsers = passportUsers.filter(({uid}) => uid !== currentUserUid);

        if (otherUsers.length === 0) {
            return null;
        }

        return (
            <Menu className={b('section')}>
                <span className={b('section-title')}>{i18n('label_users')}</span>
                {otherUsers.map((user) => (
                    <Menu.Item className={b('users-item')} key={user.uid}>
                        {this.renderUser(user)}
                    </Menu.Item>
                ))}
            </Menu>
        );
    }

    private renderLoginHistory() {
        const {loginHistory = []} = this.props;
        const {id: currentUserId} = this.getCurrentUser();

        const otherEntries = loginHistory.filter(({id}) => id !== currentUserId);

        if (otherEntries.length === 0) {
            return null;
        }

        return (
            <Menu className={b('section')}>
                <span className={b('section-title')}>{i18n('label_login-history')}</span>
                {otherEntries.map((entry) => (
                    <Menu.Item className={b('login-history-item')} key={entry.id}>
                        {this.renderLoginHistoryEntry(entry)}
                    </Menu.Item>
                ))}
            </Menu>
        );
    }

    private renderLoginButton() {
        const {internal, oauthUrl, getRetpathUrl} = this.props;

        // oauthUrl is required if cloudAuth is true
        if (this.isCloudAuth() && !oauthUrl) {
            return null;
        }

        const retpathUrl = getRetpathUrl?.(UserDropdownEventType.AccountLogin);
        const loginHref = this.isCloudAuth()
            ? `${this.getBaseUrl()}/auth/switch?redirectUrl=${encodeURIComponent(
                  // Retpath for `/auth/switch` must be origin related
                  retpathUrl || `${location.pathname}${location.search}`,
              )}`
            : `${getPassportHref(internal)}/auth?mode=add-user&retpath=${encodeURIComponent(
                  retpathUrl || location.href,
              )}`;

        return (
            <Menu className={b('section')}>
                <Menu.Item
                    className={b('login-button')}
                    title={i18n('label_account-login')}
                    href={loginHref}
                >
                    <div className={b('entry')}>
                        <div className={b('entry-icon')}>
                            <Icon data={Plus} size={16} />
                        </div>
                        <div className={b('entry-info')}>
                            <span className={b('entry-name')}>{i18n('label_account-login')}</span>
                        </div>
                    </div>
                </Menu.Item>
            </Menu>
        );
    }

    private renderUser(user: User) {
        const {
            currentOrgId,
            internal,
            onOpenUserInfo,
            getRetpathUrl,
            organizationIcon,
            organizationIconSize,
        } = this.props;
        const currentUser = this.getCurrentUser();

        const isCurrentUser = currentUser.uid === user.uid;
        const name = getUserName(user);
        const description = getUserDescription(user);

        const retpathUrl = encodeURIComponent(
            getRetpathUrl?.(UserDropdownEventType.CurrentUserLogout) || location.href,
        );

        let logoutHref: string;

        // there is no auth-ui for internal user + internal user can't be federation user at the same time,
        // that means that we logout internal and passport users from passport and others from auth
        if (this.isCloudAuth()) {
            logoutHref = `${this.getBaseUrl()}/auth/logout?redirectUrl=${retpathUrl}`;
        } else {
            const passportHref = getPassportHref(internal);

            logoutHref = `${passportHref}/passport?mode=embeddedauth&action=logout&yu=${currentUser.yandexuid}&uid=${user.uid}&retpath=${retpathUrl}`;
        }

        return (
            <div className={b('entry', {'show-buttons': isCurrentUser})}>
                <UserDropdownUser
                    className={b('user')}
                    user={user}
                    name={name}
                    description={description}
                    currentOrgId={isCurrentUser ? currentOrgId : undefined}
                    internal={internal}
                    onClick={
                        isCurrentUser
                            ? undefined
                            : this.createPassportFormHandler(
                                  user,
                                  currentUser,
                                  PassportLoginAction.SwitchUser,
                              )
                    }
                    organizationIcon={organizationIcon}
                    organizationIconSize={organizationIconSize}
                />
                <div className={b('entry-buttons')}>
                    {isCurrentUser && onOpenUserInfo && (
                        <Button
                            className={b('entry-button')}
                            title={i18n('label_user-info')}
                            view="flat-secondary"
                            onClick={onOpenUserInfo}
                        >
                            <Icon data={CircleInfo} size={18} />
                        </Button>
                    )}
                    <Button
                        className={b('entry-button')}
                        title={i18n('label_logout')}
                        href={isCurrentUser ? logoutHref : undefined}
                        view="flat-secondary"
                        onClick={
                            isCurrentUser
                                ? this.handleCurrentUserLogout
                                : this.createPassportFormHandler(
                                      user,
                                      currentUser,
                                      PassportLoginAction.Logout,
                                  )
                        }
                    >
                        <Icon data={ArrowRightFromSquare} size={18} />
                    </Button>
                </div>
            </div>
        );
    }

    private renderLoginHistoryEntry(entry: LoginHistoryEntry) {
        const {onDeleteHistoryEntry, getRetpathUrl} = this.props;
        const {
            id,
            username,
            displayName,
            email,
            avatar,
            avatarData,
            federationId,
            federationName,
            organizationId,
            organizationName,
        } = entry;

        const name = displayName || username || email;
        const description =
            organizationName || federationName || email || organizationId || federationId;
        const avatarUrl = getAvatarUrl(avatar, avatarData);

        if (!name) {
            return null;
        }

        // Retpath for `/auth/switch` must be origin related
        const retpathUrl =
            getRetpathUrl?.(UserDropdownEventType.SwitchUser) ||
            `${location.pathname}${location.search}`;
        const searchString = new URLSearchParams({sub: id, redirectUrl: retpathUrl}).toString();
        const switchUrl = `${this.getBaseUrl()}/auth/switch?${searchString}`;

        // TODO(golbahsg): Add onEvent for swithing accounts from login history
        return (
            <a className={b('entry')} href={switchUrl}>
                {avatarUrl ? (
                    <UserDropdownAvatar className={b('entry-avatar')} url={avatarUrl} size="m" />
                ) : (
                    <div className={b('entry-icon')}>
                        <Icon data={Key} size={18} />
                    </div>
                )}
                <div className={b('entry-info')}>
                    <span className={b('entry-name')}>{name}</span>
                    {description && description !== name && (
                        <span className={b('entry-description')}>{description}</span>
                    )}
                </div>
                <div className={b('entry-buttons')}>
                    {onDeleteHistoryEntry && (
                        <Button
                            className={b('entry-button')}
                            title={i18n('label_remove-login-history-entry')}
                            view="flat-secondary"
                            onClick={(event) => {
                                event.stopPropagation();
                                event.preventDefault();
                                onDeleteHistoryEntry(entry.id);
                            }}
                        >
                            <Icon data={Xmark} size={18} />
                        </Button>
                    )}
                </div>
            </a>
        );
    }

    private isCloudAuth() {
        return !this.props.internal && this.props.cloudAuth;
    }

    private getCurrentUser() {
        if (!this.props.user) {
            throw new Error('User is not provided');
        }

        return this.props.user;
    }

    private getBaseUrl() {
        return this.props.baseUrl || '';
    }

    private handleCurrentUserLogout = () => this.props.onEvent?.({type: 'currentUserLogout'});

    private createPassportFormHandler = (
        account: Account,
        currentUser: User,
        action: PassportLoginAction,
    ) => {
        return (e: React.MouseEvent<HTMLElement>) => {
            e.preventDefault();

            const {internal, onEvent, getRetpathUrl} = this.props;

            let retpath: string | undefined;

            switch (action) {
                case PassportLoginAction.SwitchUser:
                    onEvent?.({
                        type: 'switchUser',
                        payload: {account, currentUser},
                    });
                    retpath = getRetpathUrl?.(UserDropdownEventType.SwitchUser);
                    break;
                case PassportLoginAction.Logout:
                    onEvent?.({
                        type: 'accountLogout',
                        payload: {account, currentUser},
                    });
                    retpath = getRetpathUrl?.(UserDropdownEventType.AccountLogout);
                    break;
            }

            submitPassportForm({account, currentUser, action, internal, retpath});
        };
    };
}
